/**
 * Il colore di ogni widget.
 *
 * Come sul telefono, ogni widget ha la sua faccia: un colore pieno e
 * riconoscibile, cosi' si trova a colpo d'occhio anche senza leggere il
 * titolo. I tre colori del marchio SLAB fanno da capofila (rosso per il
 * fatturato, oro e verde per incassi e ricavi); intorno una famiglia calda di
 * viola, fucsia, ambra e arancio. Niente azzurri: sono i colori del marchio
 * precedente, e il controllo anti-residui li boccia.
 *
 * Due tipi di faccia:
 * - "scura": sfondo colorato pieno, testo bianco (i numeri da guardare);
 * - "chiara": sfondo tenue, testo scuro (gli elenchi da leggere).
 *
 * Le classi sono scritte per intero perche' Tailwind genera solo quelle che
 * trova scritte cosi' nel codice.
 */

import type { TipoWidget } from '../../lib/dashboard/catalogo';

export interface TemaWidget {
  /** Testo chiaro su sfondo colorato. */
  scuro: boolean;
  /** Sfondo del riquadro. */
  sfondo: string;
  /** Colore del testo di base. */
  testo: string;
  /** Il cerchio dietro l'icona del titolo. */
  icona: string;
  /** La luce sfocata nell'angolo, che da' profondita'. */
  alone: string;
  /** Il colore d'accento dentro il widget (numeri, barre, pallini). */
  accento: string;
}

const scuro = (sfondo: string, alone: string): TemaWidget => ({
  scuro: true,
  sfondo,
  testo: 'text-white',
  icona: 'bg-white/20 text-white',
  alone,
  accento: 'text-white',
});

const chiaro = (sfondo: string, icona: string, alone: string, accento: string): TemaWidget => ({
  scuro: false,
  sfondo,
  testo: 'text-neutral-900',
  icona,
  alone,
  accento,
});

export const TEMI: Readonly<Record<TipoWidget, TemaWidget>> = {
  // Da fare oggi
  'impegni-oggi': chiaro('bg-white', 'bg-primary-50 text-primary-600', 'bg-primary-100/60', 'text-primary-600'),
  'lead-da-richiamare': scuro('bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-700', 'bg-fuchsia-300/40'),
  'bozze-abbonamenti': chiaro('bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50', 'bg-amber-100 text-amber-700', 'bg-amber-200/60', 'text-amber-700'),
  'rinnovi-abbonamenti': chiaro('bg-gradient-to-br from-orange-50 via-amber-50 to-white', 'bg-orange-100 text-orange-600', 'bg-orange-200/50', 'text-orange-600'),

  // Soldi
  fatturato: scuro('bg-gradient-to-br from-primary-500 via-primary-600 to-primary-900', 'bg-rose-300/40'),
  'da-incassare': scuro('bg-gradient-to-br from-amber-400 via-orange-500 to-orange-700', 'bg-yellow-200/50'),
  'fatture-scadute': chiaro('bg-gradient-to-br from-rose-50 via-white to-white', 'bg-red-100 text-red-600', 'bg-rose-200/50', 'text-red-600'),
  'andamento-mensile': scuro('bg-gradient-to-br from-neutral-900 via-stone-900 to-neutral-800', 'bg-primary-500/30'),
  'ricavi-ricorrenti': scuro('bg-gradient-to-br from-emerald-500 via-green-600 to-green-800', 'bg-lime-200/40'),

  // Commerciale
  'nuovi-lead': scuro('bg-gradient-to-br from-fuchsia-500 via-pink-500 to-rose-600', 'bg-pink-200/50'),
  'pipeline-lead': chiaro('bg-gradient-to-br from-violet-50 via-white to-purple-50', 'bg-violet-100 text-violet-600', 'bg-violet-200/50', 'text-violet-600'),
  'lead-per-fonte': chiaro('bg-gradient-to-br from-fuchsia-50 via-white to-pink-50', 'bg-fuchsia-100 text-fuchsia-600', 'bg-fuchsia-200/50', 'text-fuchsia-600'),
  'offerte-aperte': scuro('bg-gradient-to-br from-purple-500 via-violet-600 to-purple-900', 'bg-fuchsia-300/40'),

  // Mailing e strumenti
  'ultima-campagna': scuro('bg-gradient-to-br from-fuchsia-600 via-pink-500 to-orange-400', 'bg-yellow-200/50'),
  'contatti-mailing': scuro('bg-gradient-to-br from-lime-500 via-green-500 to-emerald-700', 'bg-yellow-200/40'),
  'azioni-rapide': chiaro('bg-gradient-to-br from-white via-neutral-50 to-stone-100', 'bg-neutral-900 text-white', 'bg-primary-100/50', 'text-neutral-900'),
  nota: chiaro('bg-gradient-to-br from-yellow-100 via-yellow-50 to-amber-100', 'bg-yellow-300/70 text-amber-900', 'bg-yellow-200/70', 'text-amber-900'),
};
